import { CovanceFilter } from './../../covance-filter/covance-filter';
import { Component, ChangeDetectionStrategy, ViewEncapsulation, ChangeDetectorRef, Inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CvdChartData, CvdBandDataValue, ICvdChartEvent, AfterChartRenderedEventArgs } from '@covance/ng-cvd3';
import { isEmpty } from 'lodash';
import { SITE_LAB_RESULTS_FILTERS_STORAGE } from './filtered-lab-test-results-storage';
import { LabTestResult } from '../../lab-test-results';
import { BaseOsrChart } from './base-osr-chart';
import { formatters, UrlBuilder } from '../../core';
import { SubjectTitleRenderer } from '../../renderer/subject-title-renderer';
import { select, Selection, ScaleLinear } from 'd3';
import { ChartLegendItem } from '../../legend';
import { LabTestChartEvent } from '../../subjects/lab-tests-chart.component';
import { TooltipFormatter } from '../../shared/tooltip-formatter';
import { TooltipItem } from '../../shared/tooltip-item';
import { OsrLegendDataService } from '../../charts/osr-legend-data.service';
import { FlaggedClassNames, hideAllButSelectedSubjects, opaqueAllButSelectedSubjects, removeOpacityFromAllElements } from '../../charts/chart-helper';
import { LabTrendsChartDataValue, TrendsChartDataBuilder } from '../../charts/trends-chart-data-builder';
import { BandRenderer } from '../../charts/band-renderer';
import { tooltipClassName } from '../../core/tooltip-remover';
import { FilterStorage } from './lab-test-results-filter-storage';
import { SiteTestDataStorage, SITE_LAB_RESULTS_STORAGE } from './site-test-data-storage';
import { findSelectedSite } from '../../shared/route-helpers';

export interface VisitInfo {
    visitCode: string;
    visitTitle: string;
}

@Component({
    selector: 'lab-trends-chart',
    template: require('./lab-trends-chart.component.html'),
    styles: [require('./lab-chart.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
})
export class LabTrendsChartComponent extends BaseOsrChart {

    public resultsDictionary: { [labTestId: number]: LabTestResult };
    public bandData: CvdChartData<CvdBandDataValue>[];
    public sexLegendData: ChartLegendItem[];
    public subjectLegendData: ChartLegendItem[];
    public flaggedLegendData: ChartLegendItem[];

    private xScale: ScaleLinear<number, number>;
    private yScale: ScaleLinear<number, number>;

    private selectedTestIds: number[];
    private svgChart: Selection<SVGElement, CvdChartData<LabTrendsChartDataValue>[], HTMLElement, any> = null;

    constructor(
        @Inject(SITE_LAB_RESULTS_STORAGE) filteredStorage: SiteTestDataStorage,
        @Inject(SITE_LAB_RESULTS_FILTERS_STORAGE) private filterStorage: FilterStorage,
        changeDetectorRef: ChangeDetectorRef,
        private urlBuilder: UrlBuilder,
        private route: ActivatedRoute,
        private tooltipFormatter: TooltipFormatter,
        private legendDataService: OsrLegendDataService,
        private chartDataBuilder: TrendsChartDataBuilder,
        private bandRenderer: BandRenderer,
        private filter: CovanceFilter
    ) {
        super(filteredStorage, changeDetectorRef);
    }

    public tooltip = (key: string, x: string, y: string, value: LabTestChartEvent) => {
        const result = this.resultsDictionary[value.data.rows[0]];
        const resultData = result.currentResult;
        const visitTitle = result.visitName ? result.visitName : '';
        const unit = resultData ? resultData.unit || '' : '';
        const rangeString = resultData.rangeWithUnits;

        const subjectTitle = SubjectTitleRenderer.renderTitle({ data: result });

        const tooltipItems: TooltipItem[] = [];
        tooltipItems.push(new TooltipItem('Subject', subjectTitle));
        tooltipItems.push(new TooltipItem('Visit', visitTitle));
        const valueTooltipItem = new TooltipItem('Value', `${y} ${unit}`);
        valueTooltipItem.contentClass = `${tooltipClassName}-${value.data.colorClassName}`;
        tooltipItems.push(valueTooltipItem);
        tooltipItems.push(new TooltipItem('Range', rangeString));
        tooltipItems.push(new TooltipItem('Collection Time', formatters.formatDateTime(result.visitCollectionDate)));
        tooltipItems.push(new TooltipItem('Accession', result.accessionNumber));

        if (!isEmpty(result.flags)) {
            tooltipItems.push(new TooltipItem('Flags', result.flags.map(f => f.fullName).join(' ')));
        }

        this.tooltipFormatter.clear();
        this.tooltipFormatter.addTooltipItems(tooltipItems);
        return this.tooltipFormatter.getTooltipContent();
    }

    public goToSubject(event: ICvdChartEvent<LabTrendsChartDataValue>) {
        const labTestId = event.data.rows[0];
        const labTestResult = this.resultsDictionary[labTestId];

        if (!labTestResult.zcSubjectId) {
            return;
        }

        this.urlBuilder
            .esa()
            .site(findSelectedSite(this.route.snapshot))
            .subject({ zcSubjectId: labTestResult.zcSubjectId })
            .navigate();
    }

    public subjectsSelected(selectedItems: ChartLegendItem[]) {
        hideAllButSelectedSubjects(this.svgChart, selectedItems, this.data);
    }

    public subjectLegendMouseOver(selectedItem: ChartLegendItem) {
        opaqueAllButSelectedSubjects(this.svgChart, selectedItem, this.data);
    }

    public subjectLegendMouseOut() {
        removeOpacityFromAllElements(this.svgChart);
    }

    public afterChartRendered(event: AfterChartRenderedEventArgs) {

        if (this.svgChart === null) {
            this.svgChart = select<SVGElement, CvdChartData<LabTrendsChartDataValue>[]>('#' + event.svgId);
            this.svgChart.on('mouseup', () => {
                if (isEmpty(this.selectedTestIds)) {
                    return;
                }

                const subjectsQueryParams = this.filterStorage.subjectFilter.def.getItems(this.selectedTestIds.map(lr => this.resultsDictionary[lr]));
                this.filter.setValue(this.filterStorage.subjectFilter, subjectsQueryParams);
            });
        }

        this.xScale = event.xScale;
        this.yScale = event.yScale;

        this.bandRenderer.initHovers(event.svgId, this.xScale, this.yScale, this.resultsDictionary);

        this.setPointsColors(this.svgChart);

        const areaSvg = this.svgChart.select<SVGElement>('.cv-x.cv-axis');
        const rect = areaSvg.node().getBoundingClientRect();

        const ticks = areaSvg.selectAll<SVGElement, number>('.tick text');
        ticks.attr('hidden', function () {
            const tickRect = this.getBoundingClientRect();
            return (tickRect.left === rect.left || tickRect.right === rect.right) ? true : null;
        });
    }

    public filterSubjects(selectedItems: number[]) {
        this.selectedTestIds = selectedItems;
    }

    protected buildChartData(): CvdChartData<LabTrendsChartDataValue>[] {
        this.resultsDictionary = {};
        this.data.forEach(res => this.resultsDictionary[res.labTestId] = res);
        const cvdData = this.chartDataBuilder.buildChartData(this.currentTestData);
        return cvdData;
    }

    protected chartSpecific() {
        this.bandData = this.chartDataBuilder.buildBandData(this.currentTestData);
        this.sexLegendData = this.legendDataService.buildSexLegendData(this.currentTestData);
        this.subjectLegendData = this.legendDataService.buildSubjectLegendData(this.currentTestData);
        this.flaggedLegendData = this.legendDataService.buildFlaggedLegendData(this.currentTestData);
    }

    private setPointsColors(svgChart: Selection<SVGElement, CvdChartData<LabTrendsChartDataValue>[], HTMLElement, any>) {
        svgChart.selectAll<SVGPathElement, LabTrendsChartDataValue>('.cv-scatter g.cv-groups g.cv-group path.cv-point')
            .classed(FlaggedClassNames.high, false)
            .classed(FlaggedClassNames.low, false)
            .classed(FlaggedClassNames.other, false)
            .classed(FlaggedClassNames.nonFlagged, false)
            .each(function (data) {
                select(this).classed(data.colorClassName, true);
            });
    }
}
